import { Grid, Box, Stack, Avatar, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { RiPulseFill } from 'react-icons/ri';
import { IGlobalJobDTO, IGlobalJobSearchFilterDTO } from 'wisestep-api';
import {
  WsColors,
  WsButton,
  WsTypography,
  WsTypoColors,
  WsPagination,
  WsDetailsCard,
  WsIconCurrency,
  WsIconVisa,
  WsChip,
  WsChipVariants,
  WsIconViewJD,
  BottomActionBar,
  WsIconText,
  WsDrawer,
  WsListingLoader,
  WsDialog,
  WsTypoVariants,
  WsIconJobOutline,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../../components/noDataFoundCard';
import { openUrlInNewTab } from '../../../../../utils/openUrlInNewTab';
import { constructHotJobsQuery } from '../../../helper/ConstructHotJobsQuery';
import { hotJobsDefaultQuery } from '../../../helper/HotJobsDefaultQuery';
import useHotJobsListing from '../../hooks/useHotJobsListing';
import { IHotJobsAdvancedFilters, IHotJobsSearchFilters } from '../../types/IHotJobsSearch';

import Communication from './Communication';
import HotJobsAdvancedFilters from './HotJobsAdvancedFilters';
import HotJobsSearchFilter from './HotJobsSearchFilter';
import HotJobsSideNav from './HotJobsSideNav';

const HotJobsListing = () => {
  const defaultQuery = {
    postedInBetweenDays: 30,
    visaStatus: 'All',
    page: 0,
    includeJobsWithoutVisas: true,
    query: '',
    jobTitles: [],
  };
  const methods = useForm<IHotJobsSearchFilters & IHotJobsAdvancedFilters>({
    defaultValues: defaultQuery,
  });
  const [query, setQuery] = useState<IGlobalJobSearchFilterDTO>(hotJobsDefaultQuery);
  const page = methods.watch('page');
  const { reset, handleSubmit } = methods;
  const [loader, setLoader] = useState<boolean>(false);
  const [jobListArr, pagesCount] = useHotJobsListing(query, setLoader);
  const [viewJd, setViewJd] = useState<{ view: boolean; jobIndex: number }>({
    view: false,
    jobIndex: 0,
  });
  const [selectedJobs, setSelectedJobs] = useState<(IGlobalJobDTO & { isSelected: boolean })[]>([]);
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);
  const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');
  const [openCommunication, setOpenCommunication] = useState<boolean>(false);
  const selectedSideIndex = (index: number) => {
    switch (index) {
      case 0:
        setQuery((prev) => ({ ...prev, sourcesOfJob: undefined }));
        unSelectAllJobs();
        break;
      case 1:
        setQuery((prev) => ({ ...prev, sourcesOfJob: ['TECHFETCHSEARCH', 'DICESEARCH'] }));
        unSelectAllJobs();
        break;
      case 2:
        setQuery((prev) => ({ ...prev, sourcesOfJob: ['email', 'email1'] }));
        unSelectAllJobs();
        break;
    }
  };

  const addSelectedJob = (job: IGlobalJobDTO & { isSelected: boolean }) => {
    job.isSelected = !job.isSelected;
    if (!selectedJobs.some((existing) => existing.id === job.id)) selectedJobs.push(job);
    else selectedJobs.splice(selectedJobs.indexOf(job), 1);
    setSelectedJobs([...selectedJobs]);
  };

  const performSearch = (data: IHotJobsSearchFilters & IHotJobsAdvancedFilters) => {
    setQuery(constructHotJobsQuery(data, query));
    setOpenAdvancedFilters(false);
  };

  const selectAllJobs = () => {
    jobListArr.forEach((job) => {
      if (!job.isSelected) selectedJobs.push(job);
      job.isSelected = true;
    });
    setSelectedJobs([...selectedJobs]);
  };

  const unSelectAllJobs = () => {
    jobListArr.forEach((job) => {
      job.isSelected = false;
    });

    setSelectedJobs([]);
  };
  const onReset = () => {
    setQuery({
      ...defaultQuery,
    });
    reset({ ...defaultQuery });
    handleSubmit((data) => performSearch(data))();
  };

  return (
    <FormProvider {...methods}>
      <Grid container xs={12}>
        <Grid item xs={3}>
          <HotJobsSideNav onChange={selectedSideIndex} indexCount={pagesCount} />
        </Grid>
        <Grid item xs={9}>
          <HotJobsSearchFilter
            openAdvancedFilters={setOpenAdvancedFilters}
            performSearch={performSearch}
            onReset={onReset}
          />
          <HotJobsAdvancedFilters
            open={openAdvancedFilters}
            performSearch={performSearch}
            filterClose={() => setOpenAdvancedFilters(false)}
            onReset={onReset}
          />

          <Box my={2}>
            <WsPagination
              count={Math.ceil(pagesCount / 50)}
              onChange={(_, value) => {
                methods.setValue('page', value);
                setQuery((prev) => ({ ...prev, page: value }));
              }}
              itemsPerPage={50}
              pageNumber={page}
              defaultPage={(page ?? 0) + 1}
              isPageCount={true}
              type="Jobs"
              totalCount={pagesCount}
            />
          </Box>
          {jobListArr.length > 0 && !loader ? (
            <Box>
              {jobListArr?.map((job, index) => (
                <Box my={2} key={index}>
                  <WsDetailsCard
                    title={job.title}
                    email={job.companyEmail}
                    locationsArray={job.locations}
                    detailsArray={[
                      {
                        icon: <WsIconCurrency />,
                        labelName: job.rate ? job.rate : 'Not Available',
                        popOverText: 'Salary',
                      },
                      {
                        icon: <WsIconVisa />,
                        labelName: job.visas ? job.visas.join(',') : 'Not Available',
                        popOverText: 'Visa',
                      },
                      {
                        icon: <WsIconJobOutline />,
                        labelName: job.jobType ? job.jobType.join(',') : 'Not Available',
                        popOverText: 'Job Type',
                      },
                    ]}
                    gridXs={4}
                    skillsArray={job.skills}
                    isActiveCard={true}
                    rightChildren={
                      <WsChip
                        value={
                          job.sourceOfJob
                            ? ['email', 'email1'].includes(job.sourceOfJob)
                              ? 'EMAIL'
                              : job.sourceOfJob
                            : 'Unknown'
                        }
                        variant={WsChipVariants.PRIMARY}
                      />
                    }
                    postedByChildren={
                      <Box ml={1}>
                        <WsIconText
                          icon={<RiPulseFill color={WsColors.TheBlues500} />}
                          text={job.createdOn ? `Posted on: ${formattedDate(job.createdOn)}` : ''}
                        />
                      </Box>
                    }
                    footer={
                      <Stack
                        direction="row"
                        p={2}
                        spacing={2}
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <WsTypography variant={WsTypoVariants.CAPTION}>
                          Recruiter Details:
                          {job.senderEmail
                            ?.split('@')
                            ?.map(
                              (string, index) =>
                                string.substring(0, 3) + (index === 0 ? 'xxxx' : 'xxxx.com')
                            )
                            .join('@') ?? 'Not Available'}
                        </WsTypography>
                        <Grid container item xs={2.6} justifyContent="space-between">
                          <WsButton
                            onClick={() => {
                              setViewJd({ view: true, jobIndex: index });
                            }}
                            startIcon={<WsIconViewJD />}
                            variant="outlined"
                            size="large"
                          >
                            View JD
                          </WsButton>
                          <WsButton
                            size="large"
                            onClick={() => {
                              if (job.sourceOfJob === 'email' || job.sourceOfJob === 'email1') {
                                addSelectedJob(job);
                                setOpenCommunication(true);
                              } else {
                                openUrlInNewTab(job?.applyURL ?? '');
                              }
                            }}
                          >
                            Apply
                          </WsButton>
                        </Grid>
                      </Stack>
                    }
                    isCheckBox={true}
                    checkBoxOnclick={() => addSelectedJob(job)}
                    isSelected={
                      selectedJobs.find((existing) => existing.id === job.id)?.isSelected ?? false
                    }
                  />
                </Box>
              ))}
              <WsDrawer
                open={openCommunication}
                onClose={() => {
                  setOpenCommunication(false);
                  unSelectAllJobs();
                }}
                title="Select suitable Consultants for Job(s)"
                style={{ zIndex: '1300' }}
              >
                <Communication selectedJobs={selectedJobs} />
              </WsDrawer>

              <Box mr={2}>
                <Grid item xs={12} container justifyContent="flex-end" mt={2}>
                  {pagesCount > 20 && (
                    <WsPagination
                      count={Math.ceil(pagesCount / 50)}
                      onChange={(_, value) => {
                        methods.setValue('page', value);
                        setQuery((prev) => ({ ...prev, page: value }));
                      }}
                      itemsPerPage={50}
                      pageNumber={page}
                      defaultPage={(page ?? 0) + 1}
                      isPageCount={false}
                      type="Jobs"
                      totalCount={pagesCount}
                    />
                  )}
                </Grid>
              </Box>
            </Box>
          ) : loader ? (
            <WsListingLoader />
          ) : (
            <NoDataFoundCard text="No Jobs Found" />
          )}
        </Grid>
      </Grid>

      {selectedJobs.length > 0 && (
        <BottomActionBar>
          <Grid item container justifyContent="space-between" px={6}>
            <Grid item container xs={9} display="flex">
              <Grid item pr={2} display="flex">
                <Avatar
                  variant="square"
                  sx={{ bgcolor: WsColors.TheBlues400, width: 24, height: 24 }}
                >
                  <WsTypography color={WsTypoColors.WHITE}>{selectedJobs.length}</WsTypography>
                </Avatar>
                <WsTypography color={WsTypoColors.PRIMARY}>Job(s) Selected</WsTypography>
              </Grid>

              <Stack spacing={2} direction="row">
                <WsButton onClick={selectAllJobs}>Select All</WsButton>
                <WsButton onClick={unSelectAllJobs}>Un-Select All</WsButton>
              </Stack>
            </Grid>

            <WsButton onClick={() => setOpenCommunication(true)}>Apply</WsButton>
          </Grid>
        </BottomActionBar>
      )}
      <WsDialog
        onClose={() => setViewJd({ view: false, jobIndex: 0 })}
        openDialog={viewJd.view}
        size="medium"
      >
        <Grid item container p={4} xs={12} justifyContent="space-between" alignItems="center">
          <WsTypography variant={WsTypoVariants.TITLE}>Job Description</WsTypography>
          <WsButton onClick={() => setViewJd({ view: false, jobIndex: 0 })}>Close</WsButton>
        </Grid>
        <Stack px={5} pt={2} pb={4}>
          {jobListArr.length > 0 ? (
            <Typography
              dangerouslySetInnerHTML={{
                __html: jobListArr[viewJd.jobIndex].description
                  ? jobListArr[viewJd.jobIndex].description
                      ?.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, 'illegalscript')
                      .replace(/\n/g, '<br>') ?? ''
                  : '',
              }}
            />
          ) : null}
        </Stack>
      </WsDialog>
    </FormProvider>
  );
};

export default HotJobsListing;
